// Shell Task search. It reads only the already-authorised Task-board projection through the injected
// SurfaceApi, filters titles through task-search.ts, and hands a selected Task id to the existing modal.
// The modal performs its own re-authorised detail read; search never sends chat/model input.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.tsx';
import { useShell } from './ShellContext.tsx';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import type { BoardTaskRow } from '../surfaces/projections.ts';
import { boundTaskSearchQuery, searchTaskTitles, TASK_SEARCH_QUERY_LIMIT } from './task-search.ts';

type TaskIndexState =
  | { readonly kind: 'idle'; readonly tasks: readonly BoardTaskRow[] }
  | { readonly kind: 'loading'; readonly tasks: readonly BoardTaskRow[] }
  | { readonly kind: 'ready'; readonly tasks: readonly BoardTaskRow[] }
  | { readonly kind: 'unavailable'; readonly tasks: readonly BoardTaskRow[] };

const EMPTY_INDEX: TaskIndexState = { kind: 'idle', tasks: [] };

/** One board read per palette opening. A generation fence makes late reads after close/reopen inert. */
function useTaskSearchIndex(open: boolean): TaskIndexState {
  const api = useSurfaceApi();
  const generation = useRef(0);
  const [state, setState] = useState<TaskIndexState>(EMPTY_INDEX);

  useEffect(() => {
    generation.current += 1;
    const requestGeneration = generation.current;
    if (!open) {
      setState(EMPTY_INDEX);
      return;
    }

    setState({ kind: 'loading', tasks: [] });
    void api.getTaskBoard().then(
      (board) => {
        if (generation.current !== requestGeneration) return;
        setState({ kind: 'ready', tasks: board.filtered });
      },
      () => {
        if (generation.current !== requestGeneration) return;
        setState({ kind: 'unavailable', tasks: [] });
      },
    );
    return () => {
      if (generation.current === requestGeneration) generation.current += 1;
    };
  }, [api, open]);

  return state;
}

function SearchOutcome({
  state,
  query,
  onSelect,
}: {
  readonly state: TaskIndexState;
  readonly query: string;
  readonly onSelect: (taskId: string) => void;
}): React.JSX.Element {
  if (state.kind === 'loading') return <p role="status">Loading Tasks…</p>;
  if (state.kind === 'unavailable') return <p role="alert">Task search is unavailable.</p>;
  if (query.trim().length === 0) return <p role="status">Type a Task title to search.</p>;

  const results = searchTaskTitles(state.tasks, query);
  if (results.length === 0) return <p role="status">No Tasks found.</p>;
  return (
    <ul className="task-search-results" aria-label="Task search results">
      {results.map((task) => (
        <li key={task.id}>
          <button type="button" onClick={() => onSelect(task.id)}>
            <span className="task-search-result-title">{task.title}</span>
            <span className="task-search-result-meta">{task.state}</span>
            <span className="task-search-result-summary">{task.managerSummary}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export interface TaskSearchPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface TaskSearchDialogProps {
  readonly state: TaskIndexState;
  readonly query: string;
  readonly input: React.MutableRefObject<HTMLInputElement | null>;
  readonly onQueryChange: (value: string) => void;
  readonly onSelect: (taskId: string) => void;
  readonly onClose: () => void;
}

function TaskSearchDialog({
  state,
  query,
  input,
  onQueryChange,
  onSelect,
  onClose,
}: TaskSearchDialogProps): React.JSX.Element {
  return (
    <div
      id="task-search-palette"
      role="dialog"
      aria-label="Search tasks"
      className="task-search-palette"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="task-search-head">
        <label htmlFor="task-search-query">Search Task titles</label>
        <button type="button" aria-label="Close Task search" onClick={onClose}>
          <Icon name="x" size={14} />
        </button>
      </div>
      <input
        ref={input}
        id="task-search-query"
        type="search"
        maxLength={TASK_SEARCH_QUERY_LIMIT}
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
        autoComplete="off"
      />
      <div className="task-search-outcome">
        <SearchOutcome state={state} query={query} onSelect={onSelect} />
      </div>
      <span className="task-search-boundary-note">
        Task titles only · opens the authorised Task detail
      </span>
    </div>
  );
}

/** The bounded title-search popover; selecting a result closes search and opens the canonical Task modal. */
export function TaskSearchPalette({
  open,
  onClose,
}: TaskSearchPaletteProps): React.JSX.Element | null {
  const shell = useShell();
  const state = useTaskSearchIndex(open);
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    input.current?.focus();
  }, [open]);

  const selectTask = useCallback(
    (taskId: string) => {
      shell.openTaskModal(taskId);
      onClose();
    },
    [onClose, shell],
  );

  if (!open) return null;
  return (
    <TaskSearchDialog
      state={state}
      query={query}
      input={input}
      onQueryChange={(value) => setQuery(boundTaskSearchQuery(value))}
      onSelect={selectTask}
      onClose={onClose}
    />
  );
}
